'use client';

import { useIntl } from 'react-intl';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { type FragmentType } from '@packages/gql/generated/shopping/fragment-masking';
import type { CustomerReviewCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/CustomerReviewCardFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { customerReviewCardMessages } from './CustomerReviewCard.messages';
import { CustomerRatingIconsWithReviewCount } from '../../CustomerRatings/CustomerRatingIconsWithReviewCount/CustomerRatingIconsWithReviewCount';
import { CustomerReviewResponse } from '../CustomerReviewResponse/CustomerReviewResponse';
import { CustomerReviewTags } from '../CustomerReviewTags/CustomerReviewTags';
import { CustomerReviewCardActionsMenu } from '../CustomerReviewCardActionsMenu/CustomerReviewCardActionsMenu';

/* GraphQL */ `
  fragment CustomerReviewCardFragment on RatingReview {
    id
    created
    description
    rating
    reviewer {
      nickname
    }
    reviewTags {
      proTags {
        ...CustomerReviewTagFragment
      }
      contraTags {
        ...CustomerReviewTagFragment
      }
    }
    title
    responses {
      ...CustomerReviewResponseFragment
    }
  }
`;

export type CustomerReviewCardProps = {
  /**
   * data - the data needed to render the component
   */
  data: FragmentType<typeof CustomerReviewCardFragmentFragmentDoc>;
  /**
   * hideActionMenu - should the actions menu in the header be hidden
   */
  hideActionsMenu?: boolean;
};

/**
 * The CustomerReviewCard component displays a product review written by a customer
 */
export const CustomerReviewCard = ({ data, hideActionsMenu }: CustomerReviewCardProps) => {
  const { id, reviewer, rating, created, title, description, responses, reviewTags } =
    unmask<typeof CustomerReviewCardFragmentFragmentDoc>(data);
  const { formatMessage } = useIntl();

  return (
    <div>
      <Card>
        <CardHeader hasDivider>
          <div className="flex w-full flex-row flex-nowrap justify-between gap-1">
            <div className="mt-1">
              <CustomerRatingIconsWithReviewCount ratingValue={rating} ratingsCount={0} />
              <div className="text-size-body3 text-color-text-darktransparent mt-1">
                {formatMessage(customerReviewCardMessages.author, {
                  authorNickName: reviewer.nickname,
                  created,
                })}
              </div>
            </div>
            {!hideActionsMenu && <CustomerReviewCardActionsMenu ratingId={id} />}
          </div>
        </CardHeader>
        <CardContent className="py-2">
          <div className="mt-1 flex flex-col gap-1">
            <div className="text-size-body2">
              <div className="font-bold">{title}</div>
              <div className="whitespace-pre-wrap">{description}</div>
            </div>
            {reviewTags?.proTags?.length ? (
              <CustomerReviewTags
                data={reviewTags.proTags}
                label={formatMessage(customerReviewCardMessages.tagProLabel)}
              />
            ) : null}
            {reviewTags?.contraTags?.length ? (
              <CustomerReviewTags
                data={reviewTags.contraTags}
                label={formatMessage(customerReviewCardMessages.tagContraLabel)}
              />
            ) : null}
            {responses?.map((response, i) => (
              <CustomerReviewResponse
                data={response}
                label={formatMessage(customerReviewCardMessages.responseTitle)}
                // eslint-disable-next-line react/no-array-index-key
                key={i}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
